import { useEffect, useRef, useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { storeAudioFile, MediaValidationError, ACCEPTED_AUDIO_TYPES } from '@/lib/media';
import { fetchMusicLibrary, musicLibraryTrackUrl, selectMusicLibraryTrack, type MusicLibraryTrack } from '@/lib/musicLibrary';
import { SectionCard } from '@/components/SectionCard';
import { Slider } from '@/components/Slider';
import { Toggle } from '@/components/Toggle';
import { Button } from '@/components/Button';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { defaultMusicSettings } from '@/lib/defaults';
import type { MusicSettings, ReelProject } from '@/types';

export function MusicPanel({ project }: { project: ReelProject }) {
  const updateMusic = useWorkspaceStore((s) => s.updateMusic);
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const m = project.music.overridesShared ? project.music : shared.music;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const musicUrl = useObjectUrl(m.mediaId);

  const [libraryTracks, setLibraryTracks] = useState<MusicLibraryTrack[]>([]);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [selectingTrackId, setSelectingTrackId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMusicLibrary()
      .then((tracks) => !cancelled && setLibraryTracks(tracks))
      .catch((err) => !cancelled && setLibraryError(err instanceof Error ? err.message : 'Could not load the music library.'));
    return () => {
      cancelled = true;
    };
  }, []);

  function patch(fields: Partial<MusicSettings>) {
    updateMusic(project.id, { ...fields, overridesShared: true });
  }

  async function handleFile(file: File) {
    setError(null);
    try {
      const asset = await storeAudioFile(file);
      patch({ mediaId: asset.id, enabled: true, trimStart: 0, trimEnd: null });
    } catch (err) {
      setError(err instanceof MediaValidationError ? err.message : 'Could not process that audio file.');
    }
  }

  async function handleSelectLibraryTrack(track: MusicLibraryTrack) {
    setError(null);
    setSelectingTrackId(track.id);
    try {
      const asset = await selectMusicLibraryTrack(track);
      patch({ mediaId: asset.id, enabled: true, trimStart: 0, trimEnd: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not load "${track.title}".`);
    } finally {
      setSelectingTrackId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500 dark:text-slate-400">Music comes from shared defaults unless overridden here.</span>
        <label className="flex shrink-0 items-center gap-2">
          <input type="checkbox" checked={project.music.overridesShared} onChange={(e) => updateMusic(project.id, { overridesShared: e.target.checked })} />
          Override for this Reel
        </label>
      </div>

      <SectionCard title="Background music">
        <Toggle label="Enable background music" checked={m.enabled} onChange={(v) => patch({ enabled: v })} />

        {m.enabled && (
          <>
            <div>
              <p className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">Music library</p>
              {libraryError && <p className="text-xs text-amber-600">{libraryError}</p>}
              {!libraryError && libraryTracks.length === 0 && <p className="text-xs text-slate-400">Loading tracks…</p>}
              <ul className="space-y-2">
                {libraryTracks.map((track) => (
                  <li key={track.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">{track.title}</p>
                      <p className="text-xs text-slate-400">{track.mood}</p>
                    </div>
                    <audio controls src={musicLibraryTrackUrl(track)} className="h-8 w-40" />
                    <Button size="sm" onClick={() => handleSelectLibraryTrack(track)} disabled={selectingTrackId === track.id}>
                      {selectingTrackId === track.id ? 'Using…' : 'Use this track'}
                    </Button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-slate-400">
                These are simple placeholder loops shipped with the repo — replace the files in{' '}
                <code>public/assets/music/</code> (and update <code>manifest.json</code>) with your own
                licensed or royalty-free tracks.
              </p>
            </div>

            <div className="flex items-center gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                {m.mediaId ? 'Upload a different track' : 'Upload your own track'}
              </Button>
              {m.mediaId && (
                <Button size="sm" variant="danger" onClick={() => patch({ mediaId: null, enabled: false })}>
                  Remove
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_AUDIO_TYPES.join(',')}
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            {musicUrl && (
              <div>
                <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">Current track:</p>
                <audio controls src={musicUrl} className="w-full" />
              </div>
            )}

            <Slider label="Music volume" value={m.volume} min={0} max={1} step={0.05} formatValue={(v) => `${Math.round(v * 100)}%`} onChange={(v) => patch({ volume: v })} />
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Start time" value={m.startAt} min={0} max={30} step={0.5} formatValue={(v) => `${v}s`} onChange={(v) => patch({ startAt: v })} />
              <Slider label="Trim start" value={m.trimStart} min={0} max={30} step={0.5} formatValue={(v) => `${v}s`} onChange={(v) => patch({ trimStart: v })} />
            </div>
            <Toggle label="Loop if shorter than video" checked={m.loop} onChange={(v) => patch({ loop: v })} />
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Fade in" value={m.fadeInSeconds} min={0} max={5} step={0.25} formatValue={(v) => `${v}s`} onChange={(v) => patch({ fadeInSeconds: v })} />
              <Slider label="Fade out" value={m.fadeOutSeconds} min={0} max={5} step={0.25} formatValue={(v) => `${v}s`} onChange={(v) => patch({ fadeOutSeconds: v })} />
            </div>
            <Toggle
              label="Auto-duck under voiceover"
              checked={m.duckingEnabled}
              onChange={(v) => patch({ duckingEnabled: v })}
              tooltip="Automatically lowers music volume while the voiceover is playing."
            />
            {m.duckingEnabled && (
              <Slider label="Ducking amount" value={m.duckingAmount} min={0} max={1} step={0.05} formatValue={(v) => `${Math.round(v * 100)}%`} onChange={(v) => patch({ duckingAmount: v })} />
            )}

            <Button size="sm" variant="ghost" onClick={() => patch(defaultMusicSettings())}>
              Reset music settings
            </Button>
          </>
        )}
      </SectionCard>
    </div>
  );
}
